import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  InputAdornment,
} from "@mui/material";
import {
  Email as EmailIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  LinkedIn as LinkedInIcon,
  LocationOn as LocationIcon,
  Domain as DomainIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import type { UserData } from "./types";

interface KeyInformationSectionProps {
  userData: UserData;
  onUpdate: (field: keyof UserData, value: string | Date) => void;
  onWohnortChange: (value: string) => void;
  onArbeitsortChange: (value: string) => void;
  onSave: () => void;
}

const formatDate = (value: Date | null) =>
  value ? value.toISOString().slice(0, 10) : "";

const KeyInformationSection: React.FC<KeyInformationSectionProps> = ({
  userData,
  onUpdate,
  onWohnortChange,
  onArbeitsortChange,
  onSave,
}) => {
  const { t } = useTranslation();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasPendingChange = useRef(false);

  const scheduleAutoSave = useCallback(() => {
    hasPendingChange.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      hasPendingChange.current = false;
      onSave();
    }, 2500);
  }, [onSave]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (hasPendingChange.current) onSave();
    };
  }, [onSave]);

  const handleFieldChange = (field: keyof UserData, value: string | Date) => {
    onUpdate(field, value);
    scheduleAutoSave();
  };

  const handleWohnort = (value: string) => {
    onWohnortChange(value);
    scheduleAutoSave();
  };

  const handleArbeitsort = (value: string) => {
    onArbeitsortChange(value);
    scheduleAutoSave();
  };
  const initials = userData.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <Paper
      sx={{
        p: 2.5,
        bgcolor: "#fff",
        borderRadius: 2,
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          color: "text.secondary",
          mb: 2
        }}>
        {t("coachingNavigator.clientOverview.keyInformation").toUpperCase()}
      </Typography>
      <Box sx={{ display: "flex", gap: 2.5 }}>
        <Avatar
          src={userData.picture || undefined}
          sx={{
            width: 80,
            height: 80,
            bgcolor: "primary.main",
            fontSize: "1.25rem",
            fontWeight: 600,
          }}
        >
          {initials}
        </Avatar>
        <Box
          sx={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 2,
          }}
        >
          <TextField
            label={t("coachingNavigator.clientOverview.fullName")}
            size="small"
            value={userData.name}
            onChange={(e) => handleFieldChange("name", e.target.value)}
            fullWidth
            required
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} slotProps={{ htmlInput: { maxLength: 100 } }}
          />
          <TextField
            label={t("coachingNavigator.clientOverview.company")}
            value={userData.company}
            onChange={(e) => handleFieldChange("company", e.target.value)}
            fullWidth
            size="small"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} slotProps={{ input: {
              startAdornment: (
                <InputAdornment position="start">
                  <BusinessIcon fontSize="small" />
                </InputAdornment>
              ),
            }, htmlInput: { maxLength: 150 } }}
          />
          <TextField
            label={t("coachingNavigator.clientOverview.role")}
            value={userData.role}
            onChange={(e) => handleFieldChange("role", e.target.value)}
            fullWidth
            size="small"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} slotProps={{ input: {
              startAdornment: (
                <InputAdornment position="start">
                  <WorkIcon fontSize="small" />
                </InputAdornment>
              ),
            }, htmlInput: { maxLength: 100 } }}
          />
          <TextField
            label={t("coachingNavigator.clientOverview.email")}
            type="email"
            value={userData.email}
            onChange={(e) => handleFieldChange("email", e.target.value)}
            fullWidth
            required
            size="small"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} slotProps={{ input: {
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon fontSize="small" />
                </InputAdornment>
              ),
            }, htmlInput: { maxLength: 254 } }}
          />
          <TextField
            label={t("coachingNavigator.clientOverview.linkedIn")}
            value={userData.linkedIn}
            onChange={(e) => handleFieldChange("linkedIn", e.target.value)}
            fullWidth
            size="small"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} slotProps={{ input: {
              startAdornment: (
                <InputAdornment position="start">
                  <LinkedInIcon fontSize="small" />
                </InputAdornment>
              ),
            }, htmlInput: { maxLength: 2048 } }}
          />
          <TextField
            label={t("coachingNavigator.clientOverview.dateOfBirth")}
            type="date"
            value={formatDate(userData.dateOfBirth)}
            onChange={(e) =>
              handleFieldChange("dateOfBirth", new Date(e.target.value))
            }
            fullWidth
            size="small"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label={t("coachingNavigator.clientOverview.wohnort")}
            value={userData.wohnort}
            onChange={(e) => handleWohnort(e.target.value)}
            fullWidth
            size="small"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} slotProps={{ input: {
              startAdornment: (
                <InputAdornment position="start">
                  <LocationIcon fontSize="small" />
                </InputAdornment>
              ),
            }, htmlInput: { maxLength: 100 } }}
          />
          <TextField
            label={
              !userData.arbeitsortOverridden && userData.wohnort
                ? t("coachingNavigator.clientOverview.arbeitsortAuto")
                : t("coachingNavigator.clientOverview.arbeitsort")
            }
            value={userData.arbeitsort}
            onChange={(e) => handleArbeitsort(e.target.value)}
            fullWidth
            size="small"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} slotProps={{ input: {
              startAdornment: (
                <InputAdornment position="start">
                  <DomainIcon fontSize="small" />
                </InputAdornment>
              ),
            }, htmlInput: { maxLength: 100 } }}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default KeyInformationSection;
